import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  apiErrText, rmb, parsePricingJson, previewBuildup,
  staffWiPriceLine, staffWiPriceComplete, staffWiQuotations, staffWiAddQuotation, staffWiConfirmQuotations,
  instructionDocumentUrl,
  TIER_LABEL, VARIATION_STANDARD_LABEL,
  type InstructionDetail, type PriceConfirmation, type PriceLineInput,
  type PricingComponent, type PricingTierKey, type QuotationsView, type WorkInstructionLine, type WorkItem,
} from '../lib/api';
import { toast } from './ui/Toast';

/**
 * 认价（审核）— 预算's per-line pricing sheet for one 工程指令 (合同 4.1.2 的四级瀑布).
 *
 * The sheet exists because 4.1.2 is a WATERFALL, not a price: ① same feature → the original rate,
 * ② similar → the original rate adjusted, ③ neither → negotiate, ④ no agreement → build the rate up
 * from the Beijing 消耗量标准 with 下浮10% then 管理费利润3%. Which rung you are on changes what you must
 * evidence, so the form changes with the radio rather than showing every field at once.
 *
 * The live ④ preview mirrors the server's arithmetic (base → −10% → +3% → × qty) so the number moves
 * while 预算 types — but `instructions/pricing.ts` recomputes and WINS, which is why every preview says
 * 「以服务器重算为准」 and the saved `pricing_json.steps` (server-produced) is what gets rendered after.
 *
 * MONEY SURFACE — never rendered for site_pm. InstructionsPanel branches on the role before mounting it.
 */

const TIERS: PricingTierKey[] = ['identical', 'similar', 'negotiated', 'buildup'];
const STANDARDS = Object.keys(VARIATION_STANDARD_LABEL);
const COMPONENT_KIND_LABEL: Record<PricingComponent['kind'], string> = {
  labor: '人工 / Labour', material: '材料 / Material', machinery: '机械 / Machinery',
};

/** States in which the sheet is editable at all (§3): quoted / priced / disputed / served-forfeited. */
export function canPriceInstruction(d: InstructionDetail): boolean {
  const s = d.instruction.state;
  if (s === 'quoted' || s === 'priced' || s === 'disputed') return true;
  // 9.1.3 — 乙方 let the 7 days lapse, so 预算 prices it unilaterally off a `served` row.
  return s === 'served' && !!d.instruction.quote_forfeited_at;
}

export default function InstructionPricingSheet({ detail, workItems, onChanged }: {
  detail: InstructionDetail;
  workItems: WorkItem[];
  onChanged: () => void;
}) {
  const wi = detail.instruction;
  const [busy, setBusy] = useState(false);
  const editable = canPriceInstruction(detail) && wi.state !== 'priced';
  // `lines_priced` is the server's own count (priced_at IS NOT NULL) — a client-side "has a rate"
  // heuristic would call a legitimately zero-valued 减项 line unpriced.
  const totals = detail.totals;
  const unpricedCount = totals ? totals.lines_total - totals.lines_priced : detail.lines.filter((l) => !l.priced_at).length;
  const totalAudited = totals?.audited_rmb ?? detail.lines.reduce((s, l) => s + (l.audited_amount_rmb ?? 0), 0);
  const totalDeclared = totals?.declared_rmb ?? detail.lines.reduce((s, l) => s + (l.declared_amount_rmb ?? 0), 0);
  // The OPEN 认价单 — the detail read sends every version, newest last.
  const pcf: PriceConfirmation | null = (detail.price_confirmations ?? []).find((p) => p.status === 'open')
    ?? (detail.price_confirmations ?? []).slice(-1)[0] ?? null;

  async function complete() {
    setBusy(true);
    try {
      await staffWiPriceComplete(wi.id);
      toast('已完成定价，等待商务批准 / priced — awaiting 商务 approval', { kind: 'success' });
      onChanged();
    } catch (e) { toast(apiErrText(e), { kind: 'error' }); }
    finally { setBusy(false); }
  }

  return (
    <div className="card" style={{ marginBottom: 10 }}>
      <div className="row-wrap" style={{ alignItems: 'center', gap: 10, marginBottom: 6 }}>
        <strong>💴 认价审核 / Pricing sheet</strong>
        <span className="small muted">合同 4.1.2 变更估价四级：相同 → 类似 → 协商 → 组价 / the 4.1.2 waterfall</span>
        <span style={{ flex: 1 }} />
        <span className="small">申报 / declared <strong>{rmb(totalDeclared)}</strong></span>
        <span className="small">审核 / audited <strong>{rmb(totalAudited)}</strong></span>
      </div>

      {!canPriceInstruction(detail) && (
        <div className="small muted" style={{ marginBottom: 8 }}>
          当前状态不可定价（需「已报价」「已审核」「有异议」，或报价逾期失权后的「已送达」）。/ pricing opens once the order is quoted, re-priced, disputed, or its quote window has lapsed.
        </div>
      )}

      {detail.lines.map((line, i) => (
        <LineCard pricingDefaults={(detail as { pricing_defaults?: { discount_pct: number; overhead_profit_pct: number } }).pricing_defaults} key={line.id} wiid={wi.id} line={line} no={i + 1} occurredOn={wi.occurred_on}
          defaultStandard={wi.variation_standard} workItems={workItems} editable={editable} onChanged={onChanged} />
      ))}

      <div className="row-wrap" style={{ alignItems: 'center', gap: 10, marginTop: 8 }}>
        <button className="btn-primary" style={{ minHeight: 44 }}
          disabled={busy || !editable || unpricedCount > 0}
          title={unpricedCount > 0 ? `还有 ${unpricedCount} 行未定价 / ${unpricedCount} line(s) still unpriced` : '完成定价并生成认价单 / close pricing and open the 认价单'}
          onClick={complete}>
          ✓ 完成定价 / Complete pricing
        </button>
        {unpricedCount > 0 && <span className="small muted">还有 {unpricedCount} 行未定价 / {unpricedCount} line(s) unpriced</span>}
      </div>

      {/* after price-complete: the 认价单 v(n) summary + its document */}
      {pcf && (
        <div className="card" style={{ marginTop: 10, background: 'var(--accent-soft)' }}>
          <div className="row-wrap" style={{ alignItems: 'center', gap: 10 }}>
            <strong>认价单 v{pcf.version} / Price confirmation v{pcf.version}</strong>
            <span className={`pill small ${pcf.status === 'confirmed' ? 'pill-good' : pcf.status === 'disputed' ? 'pill-bad' : pcf.status === 'superseded' ? 'pill-muted' : 'pill-stage'}`}>
              {pcf.status}
            </span>
            <span style={{ flex: 1 }} />
            <a className="btn small" style={{ minHeight: 44 }} href={instructionDocumentUrl(wi.id, 'price_confirmation')}>⬇ 认价单 .docx</a>
          </div>
          <div className="row-wrap small" style={{ gap: 14, marginTop: 6 }}>
            <span>申报合计 / declared {rmb(pcf.total_declared_rmb)}</span>
            <span>审核合计 / audited <strong>{rmb(pcf.total_audited_rmb)}</strong></span>
            {pcf.additions_rmb != null && <span>增项 {rmb(pcf.additions_rmb)}</span>}
            {pcf.omissions_rmb != null && <span>减项 {rmb(pcf.omissions_rmb)}</span>}
          </div>
          <div className="small muted" style={{ marginTop: 4 }}>{pcf.policy_label}</div>
        </div>
      )}
    </div>
  );
}

// ── one line ────────────────────────────────────────────────────────────────────────────────────

function LineCard({ wiid, line, no, occurredOn, defaultStandard, workItems, editable, onChanged, pricingDefaults }: {
  pricingDefaults?: { discount_pct: number; overhead_profit_pct: number };
  wiid: string;
  line: WorkInstructionLine;
  /** 1-based row number for display. `line.seq` is 0-based in the DB (instructions/service.ts writeLines),
   *  so showing it raw prints 「0. 名称」 — report it rather than reproduce it. */
  no: number;
  occurredOn: string;
  defaultStandard: string | null;
  workItems: WorkItem[];
  editable: boolean;
  onChanged: () => void;
}) {
  const saved = parsePricingJson(line.pricing_json);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const [tier, setTier] = useState<PricingTierKey | ''>((line.tier as PricingTierKey) ?? '');
  const [sourceWorkItemId, setSourceWorkItemId] = useState(line.source_work_item_id ?? '');
  const [adjMode, setAdjMode] = useState<'pct' | 'rmb'>('pct');
  const [adjPct, setAdjPct] = useState<string>(saved?.adjustment_pct != null ? String(saved.adjustment_pct) : '');
  const [adjRmb, setAdjRmb] = useState<string>(saved?.adjustment_rmb != null ? String(saved.adjustment_rmb) : '');
  const [adjReason, setAdjReason] = useState(saved?.adjustment_reason ?? '');
  const [negRate, setNegRate] = useState<string>(tier === 'negotiated' && saved?.unit_rate_rmb != null ? String(saved.unit_rate_rmb) : '');
  const [negWith, setNegWith] = useState(saved?.negotiated_with ?? '');
  const [negOn, setNegOn] = useState(saved?.negotiated_on ?? '');
  const [minutes, setMinutes] = useState(saved?.minutes_md ?? '');
  const [standard, setStandard] = useState(saved?.standard ?? defaultStandard ?? '2021_xiushan');
  // 造价信息月份 pre-fills from the occurrence month; changing it demands a written reason (G9).
  const occurredMonth = occurredOn.slice(0, 7);
  const [priceMonth, setPriceMonth] = useState(saved?.price_month ?? occurredMonth);
  const [overrideReason, setOverrideReason] = useState(saved?.price_month_override_reason ?? '');
  const [components, setComponents] = useState<PricingComponent[]>(saved?.components ?? []);
  const [internalNote, setInternalNote] = useState(line.internal_note_md ?? '');
  const [medianFor, setMedianFor] = useState<string | null>(null);

  const qty = line.qty ?? 0;
  // review 9/18: mirror the GM's numbers (管理 → 工程指令设置), not a hardcoded 10% / 3%
  const pd = pricingDefaults;
  const preview = useMemo(() => previewBuildup(components, qty, pd?.discount_pct ?? 10, pd?.overhead_profit_pct ?? 3), [components, qty, pd?.discount_pct, pd?.overhead_profit_pct]);
  const monthMismatch = priceMonth !== occurredMonth;

  async function save() {
    if (!tier) { setErr('请先选择定价档次 / pick a tier first'); return; }
    if (tier === 'similar' && !adjReason.trim()) { setErr('② 调整单价必须填写调整理由 / an adjustment needs a written reason'); return; }
    if (tier === 'buildup' && monthMismatch && !overrideReason.trim()) {
      setErr('造价信息月份与变更产生月份不一致，请填写理由 / a different price month needs a written reason'); return;
    }
    const body: PriceLineInput = {
      tier,
      internal_note_md: internalNote.trim() || null,
      ...(tier === 'identical' || tier === 'similar' ? { source_work_item_id: sourceWorkItemId || null } : {}),
      ...(tier === 'similar'
        ? (adjMode === 'pct'
          ? { adjustment_pct: adjPct === '' ? null : Number(adjPct), adjustment_reason: adjReason.trim() }
          : { adjustment_rmb: adjRmb === '' ? null : Number(adjRmb), adjustment_reason: adjReason.trim() })
        : {}),
      ...(tier === 'negotiated' ? {
        negotiated_unit_rate_rmb: negRate === '' ? null : Number(negRate),
        negotiated_with: negWith.trim() || null, negotiated_on: negOn || null, minutes_md: minutes.trim() || null,
      } : {}),
      ...(tier === 'buildup' ? {
        buildup: { standard, price_month: priceMonth, components },
        ...(monthMismatch ? { override_reason: overrideReason.trim() } : {}),
      } : {}),
    };
    setBusy(true); setErr(null);
    try {
      await staffWiPriceLine(wiid, line.id, body);
      toast(`第 ${line.seq} 行已定价 / line ${line.seq} priced`, { kind: 'success' });
      setOpen(false);
      onChanged();
    } catch (e) { setErr(apiErrText(e)); }
    finally { setBusy(false); }
  }

  function setComponent(i: number, patch: Partial<PricingComponent>) {
    setComponents((cs) => cs.map((c, j) => (j === i ? { ...c, ...patch } : c)));
  }

  return (
    <div className="card" style={{ marginBottom: 8, background: '#fff' }}>
      <div className="row-wrap" style={{ alignItems: 'center', gap: 10 }}>
        <strong>{no}. {line.item_label}</strong>
        <span className="small muted">{line.qty ?? '—'} {line.unit ?? ''}</span>
        {line.declared_unit_rate_rmb != null && <span className="small">申报 / declared {rmb(line.declared_unit_rate_rmb)}/{line.unit ?? '—'}</span>}
        {line.tier && <span className="pill pill-stage small" title={TIER_LABEL[line.tier]}>{TIER_LABEL[line.tier].slice(0, 1)} 已定价</span>}
        <span style={{ flex: 1 }} />
        {line.audited_amount_rmb != null && <strong>{rmb(line.audited_amount_rmb)}</strong>}
        {editable && (
          <button className="small" style={{ minHeight: 44 }} onClick={() => setOpen((v) => !v)}>
            {open ? '收起 / Close' : line.tier ? '改价 / Re-price' : '定价 / Price'}
          </button>
        )}
      </div>

      {line.measurement_calc_md && <div className="small muted">工程量计算式 / measurement: {line.measurement_calc_md}</div>}

      {/* server-produced steps — the arithmetic 乙方 is entitled to check (G9) */}
      {saved && (
        <div className="small" style={{ marginTop: 6, background: 'var(--good-soft)', padding: '6px 8px', borderRadius: 6 }}>
          <div className="muted">{TIER_LABEL[saved.tier]}</div>
          {saved.steps.map((s, i) => <div key={i}>· {s}</div>)}
          {saved.price_month && <div className="muted">造价信息月份 / price month {saved.price_month}{saved.price_month_override_reason ? `（${saved.price_month_override_reason}）` : ''}</div>}
        </div>
      )}
      {line.internal_note_md && (
        <div className="small" style={{ marginTop: 4, color: 'var(--muted)' }}>
          🔒 内部备注（不对劳务显示）/ internal, never shown to the contractor: {line.internal_note_md}
        </div>
      )}

      {open && (
        <div style={{ marginTop: 10, borderTop: '1px solid var(--rule)', paddingTop: 10 }}>
          {/* ── the waterfall, in the contract's own words ── */}
          <div className="field">
            <label>定价档次 / pricing tier（合同 4.1.2）</label>
            {TIERS.map((t) => (
              <label key={t} className="row" style={{ gap: 6, alignItems: 'flex-start', marginBottom: 4, minHeight: 44 }}>
                <input type="radio" name={`tier-${line.id}`} checked={tier === t} onChange={() => setTier(t)} />
                <span className="small">{TIER_LABEL[t]}</span>
              </label>
            ))}
          </div>

          {(tier === 'identical' || tier === 'similar') && (
            <div className="field">
              <label>原合同清单项 / source BOQ line（服务器据此取原固定综合单价）</label>
              <select value={sourceWorkItemId} onChange={(e) => setSourceWorkItemId(e.target.value)} style={{ minHeight: 44, width: '100%' }}>
                <option value="">自动匹配 / let the server resolve it</option>
                {workItems.map((w) => (
                  <option key={w.id} value={w.id}>{w.seq_label ? `${w.seq_label} ` : ''}{w.item_label}{w.unit ? ` · ${w.unit}` : ''}</option>
                ))}
              </select>
              {saved?.original_unit_rate_rmb != null && (
                <div className="small muted">已取原单价 / resolved original rate: <strong>{rmb(saved.original_unit_rate_rmb)}</strong>{saved.source_work_item_id ? ` · ${workItems.find((w) => w.id === saved.source_work_item_id)?.item_label ?? saved.source_work_item_id}` : ''}</div>
              )}
            </div>
          )}

          {tier === 'similar' && (
            <>
              <div className="row-wrap" style={{ gap: 8, alignItems: 'flex-end' }}>
                <div className="field" style={{ marginBottom: 0 }}>
                  <label>调整方式 / adjustment</label>
                  <select value={adjMode} onChange={(e) => setAdjMode(e.target.value as 'pct' | 'rmb')} style={{ minHeight: 44 }}>
                    <option value="pct">按百分比 / by %</option>
                    <option value="rmb">按金额 / by ¥</option>
                  </select>
                </div>
                <div className="field" style={{ marginBottom: 0 }}>
                  <label>{adjMode === 'pct' ? '调整幅度 % / adjustment %' : '调整金额 ¥ / adjustment ¥'}</label>
                  {adjMode === 'pct'
                    ? <input type="number" step="0.01" value={adjPct} onChange={(e) => setAdjPct(e.target.value)} style={{ minHeight: 44 }} placeholder="例 12 = +12%" />
                    : <input type="number" step="0.01" value={adjRmb} onChange={(e) => setAdjRmb(e.target.value)} style={{ minHeight: 44 }} />}
                </div>
              </div>
              <div className="field">
                <label>调整理由（必填 · 项目特征差异）/ reason (required — what differs)</label>
                <input value={adjReason} onChange={(e) => setAdjReason(e.target.value)} style={{ minHeight: 44 }}
                  placeholder="例：同做法，基层由混凝土改为加气块，人工增加" />
              </div>
            </>
          )}

          {tier === 'negotiated' && (
            <>
              <div className="row-wrap" style={{ gap: 8, alignItems: 'flex-end' }}>
                <div className="field" style={{ marginBottom: 0 }}>
                  <label>协商单价 ¥ / negotiated unit rate</label>
                  <input type="number" step="0.01" value={negRate} onChange={(e) => setNegRate(e.target.value)} style={{ minHeight: 44 }} />
                </div>
                <div className="field" style={{ marginBottom: 0 }}>
                  <label>协商对象 / negotiated with</label>
                  <input value={negWith} onChange={(e) => setNegWith(e.target.value)} style={{ minHeight: 44 }} placeholder="劳务方负责人姓名" />
                </div>
                <div className="field" style={{ marginBottom: 0 }}>
                  <label>协商日期 / date</label>
                  <input type="date" value={negOn} onChange={(e) => setNegOn(e.target.value)} style={{ minHeight: 44 }} />
                </div>
              </div>
              <div className="field">
                <label>协商纪要 / minutes</label>
                <textarea value={minutes} onChange={(e) => setMinutes(e.target.value)} rows={2} />
              </div>
            </>
          )}

          {tier === 'buildup' && (
            <>
              <div className="row-wrap" style={{ gap: 8, alignItems: 'flex-end' }}>
                <div className="field" style={{ marginBottom: 0, flex: '1 1 260px' }}>
                  <label>消耗量标准 / consumption standard</label>
                  <select value={standard} onChange={(e) => setStandard(e.target.value)} style={{ minHeight: 44, width: '100%' }}>
                    {STANDARDS.map((k) => <option key={k} value={k}>{VARIATION_STANDARD_LABEL[k]}</option>)}
                  </select>
                </div>
                <div className="field" style={{ marginBottom: 0 }}>
                  <label>造价信息月份 / 《北京工程造价信息》 issue</label>
                  <input type="month" value={priceMonth} onChange={(e) => setPriceMonth(e.target.value)} style={{ minHeight: 44 }} />
                </div>
              </div>
              {monthMismatch && (
                <div className="field">
                  <label style={{ color: 'var(--bad)' }}>与变更产生月份（{occurredMonth}）不一致 — 请填写理由（必填）/ price-month override reason (required)</label>
                  <input value={overrideReason} onChange={(e) => setOverrideReason(e.target.value)} style={{ minHeight: 44 }} />
                </div>
              )}

              <div className="field">
                <label>组价明细 人工 / 材料 / 机械 — components</label>
                <div className="cmp-wrap">
                  <table className="cmp">
                    <thead>
                      <tr>
                        <th>类别</th><th>名称</th><th>数量</th><th>单位</th><th>单价 ¥</th><th>来源</th><th>小计</th><th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {components.map((c, i) => (
                        <tr key={i}>
                          <td>
                            <select value={c.kind} onChange={(e) => setComponent(i, { kind: e.target.value as PricingComponent['kind'] })} style={{ minHeight: 44 }}>
                              {(['labor', 'material', 'machinery'] as const).map((k) => <option key={k} value={k}>{COMPONENT_KIND_LABEL[k]}</option>)}
                            </select>
                          </td>
                          <td><input value={c.label} onChange={(e) => setComponent(i, { label: e.target.value })} style={{ minHeight: 44, minWidth: 120 }} /></td>
                          <td><input type="number" step="0.001" value={c.qty ?? ''} onChange={(e) => setComponent(i, { qty: Number(e.target.value) })} style={{ minHeight: 44, width: 84 }} /></td>
                          <td><input value={c.unit ?? ''} onChange={(e) => setComponent(i, { unit: e.target.value })} style={{ minHeight: 44, width: 70 }} /></td>
                          <td><input type="number" step="0.01" value={c.unit_price_rmb ?? ''} onChange={(e) => setComponent(i, { unit_price_rmb: Number(e.target.value) })} style={{ minHeight: 44, width: 96 }} /></td>
                          <td>
                            <select value={c.source ?? 'zaojia_info'} onChange={(e) => setComponent(i, { source: e.target.value as PricingComponent['source'] })} style={{ minHeight: 44 }}>
                              <option value="zaojia_info">造价信息 / 造价信息价</option>
                              <option value="quotation_median">询价中位数 / quotation median</option>
                              <option value="contract">合同 / contract</option>
                              <option value="standard">标准 / standard</option>
                            </select>
                            {c.source === 'quotation_median' && (
                              <button className="small" style={{ minHeight: 44, marginTop: 4 }} onClick={() => setMedianFor(c.label)}>中位数 / median…</button>
                            )}
                          </td>
                          <td>{rmb((Number(c.qty) || 0) * (Number(c.unit_price_rmb) || 0))}</td>
                          <td><button className="small" style={{ minHeight: 44 }} onClick={() => setComponents((cs) => cs.filter((_, j) => j !== i))}>×</button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <button className="small" style={{ minHeight: 44, marginTop: 6 }}
                  onClick={() => setComponents((cs) => [...cs, { kind: 'labor', label: '', qty: 1, unit: '工日', unit_price_rmb: null, source: 'zaojia_info' }])}>
                  ＋ 增加一项 / Add a component
                </button>
              </div>

              {/* live preview — the SAME order the server uses, and it says so */}
              <div className="small" style={{ background: 'var(--warn-soft)', padding: '8px 10px', borderRadius: 6, marginBottom: 8 }}>
                <div><strong>试算 / preview</strong> — 以服务器重算为准 / the server recomputes and wins</div>
                {preview.steps.map((s, i) => <div key={i}>· {s}</div>)}
              </div>

              {medianFor != null && (
                <QuotationMedianHelper wiid={wiid} lineId={line.id} materialLabel={medianFor}
                  onClose={() => setMedianFor(null)}
                  onPick={(v) => {
                    setComponents((cs) => cs.map((c) => (c.label === medianFor ? { ...c, unit_price_rmb: v, source: 'quotation_median' } : c)));
                    setMedianFor(null);
                  }} />
              )}
            </>
          )}

          <div className="field">
            <label>内部备注（不对劳务显示）/ internal note — never serialised to the portal</label>
            <input value={internalNote} onChange={(e) => setInternalNote(e.target.value)} style={{ minHeight: 44 }} />
          </div>

          {err && <div className="small" style={{ color: 'var(--bad)', marginBottom: 6 }}>{err}</div>}
          <button className="btn-primary" style={{ minHeight: 44 }} disabled={busy || !tier} onClick={save}>保存本行定价 / Save line</button>
        </div>
      )}
    </div>
  );
}

/**
 * 「中位数」 helper — 4.1.2 makes a quoted median binding only 「经发包人书面确认」, so this lists the
 * quotations for one material, shows the median, and offers to record the employer's confirmation.
 */
function QuotationMedianHelper({ wiid, lineId, materialLabel, onClose, onPick }: {
  wiid: string; lineId: string; materialLabel: string; onClose: () => void; onPick: (v: number) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [party, setParty] = useState<'wtl' | 'labor'>('wtl');
  const [supplier, setSupplier] = useState('');
  const [price, setPrice] = useState('');
  // The MEDIAN is the server's, not ours: 4.1.2 only lets it bind once there are ≥ min_quotations per
  // party AND the employer has confirmed in writing, and `materials[]` already carries that verdict plus
  // the bilingual `blocked_reason`. Recomputing it here would be a second, quieter rule.
  const { data, refetch } = useQuery<QuotationsView>({
    queryKey: ['wi-quotations', wiid],
    queryFn: () => staffWiQuotations(wiid),
  });
  const rows = (data?.quotations ?? []).filter((r) => r.material_label === materialLabel);
  const summary = (data?.materials ?? []).find((m) => m.material_label === materialLabel) ?? null;
  const median = summary?.complete ? summary.median_rmb : null;

  async function run(fn: () => Promise<unknown>, okMsg: string) {
    setBusy(true);
    try { await fn(); toast(okMsg, { kind: 'success' }); await refetch(); }
    catch (e) { toast(apiErrText(e), { kind: 'error' }); }
    finally { setBusy(false); }
  }

  return (
    <div className="card drawer" style={{ borderColor: 'var(--accent)', marginBottom: 8 }}>
      <div className="row-wrap" style={{ alignItems: 'center', gap: 8 }}>
        <strong>询价中位数 / Quotation median · {materialLabel || '（请先填写名称）'}</strong>
        <span style={{ flex: 1 }} />
        <button className="small" style={{ minHeight: 44 }} onClick={onClose}>× 关闭 / Close</button>
      </div>
      <div className="small muted" style={{ margin: '4px 0 8px' }}>
        合同 4.1.2：双方各询价不少于 3 家，取中位数，且须经发包人书面确认。/ at least 3 quotations per party; the median binds only once the employer confirms in writing.
      </div>
      <div className="row-wrap small" style={{ gap: 12, marginBottom: 6 }}>
        <span>我方 / WTL {summary?.wtl_count ?? 0} 家</span>
        <span>劳务方 / contractor {summary?.labor_count ?? 0} 家</span>
        <span>发包人书面确认 / employer confirmed {summary?.employer_confirmed ? '✓' : '—'}</span>
        <span>中位数 / median <strong>{median != null ? rmb(median) : '—'}</strong></span>
      </div>
      {summary?.blocked_reason && (
        <div className="small" style={{ color: 'var(--bad)', marginBottom: 6 }}>{summary.blocked_reason}</div>
      )}
      <div className="cmp-wrap" style={{ marginBottom: 8 }}>
        <table className="cmp">
          <thead><tr><th>方</th><th>供应商</th><th>单价</th><th>日期</th><th>发包人确认</th></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id}>
                <td>{r.party === 'wtl' ? '我方' : '劳务方'}</td>
                <td>{r.supplier_name ?? '—'}</td>
                <td>{rmb(r.quoted_unit_price_rmb)}</td>
                <td>{r.quoted_on ?? '—'}</td>
                <td>{r.employer_confirmed_at ? '✓' : '—'}</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={5} className="muted">暂无询价记录 / no quotations recorded yet</td></tr>}
          </tbody>
        </table>
      </div>
      <div className="row-wrap" style={{ gap: 8, alignItems: 'flex-end' }}>
        <div className="field" style={{ marginBottom: 0 }}>
          <label>询价方 / party</label>
          <select value={party} onChange={(e) => setParty(e.target.value as 'wtl' | 'labor')} style={{ minHeight: 44 }}>
            <option value="wtl">我方 / WTL</option>
            <option value="labor">劳务方 / contractor</option>
          </select>
        </div>
        <div className="field" style={{ marginBottom: 0 }}><label>供应商 / supplier</label><input value={supplier} onChange={(e) => setSupplier(e.target.value)} style={{ minHeight: 44 }} /></div>
        <div className="field" style={{ marginBottom: 0 }}><label>单价 ¥</label><input type="number" step="0.01" value={price} onChange={(e) => setPrice(e.target.value)} style={{ minHeight: 44, width: 110 }} /></div>
        <button className="small" style={{ minHeight: 44 }} disabled={busy || !materialLabel || !price}
          onClick={() => run(() => staffWiAddQuotation(wiid, {
            material_label: materialLabel, party, supplier_name: supplier || null,
            quoted_unit_price_rmb: Number(price), quoted_on: new Date().toISOString().slice(0, 10), line_id: lineId,
          }), '询价已记录 / quotation recorded')}>＋ 记录询价 / Add</button>
        <button className="small" style={{ minHeight: 44 }} disabled={busy || !materialLabel}
          onClick={() => run(() => staffWiConfirmQuotations(wiid, materialLabel), '已记录发包人书面确认 / employer confirmation recorded')}>
          ✓ 发包人书面确认 / Employer confirms
        </button>
        <button className="btn-primary small" style={{ minHeight: 44 }} disabled={median == null} onClick={() => median != null && onPick(median)}>
          用中位数 / Use the median
        </button>
      </div>
    </div>
  );
}
